import React, { useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import DataTable from "app/components/DataTable";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import ActionBar from "../ActionBar";
import {
  selectQuoteStatusLabelKey,
  selectQuotesList,
  selectQuotesLoading,
} from "../../redux/selector";
import { quotesActions } from "../../redux/slice";
import { Quote, QuoteStatus } from "../../types";
import { useStyles } from "./styles";

const getLocale = (language: string) => {
  return language === "hw" || language === "he" ? "he-IL" : "en-US";
};

const formatCurrency = (value: number) => {
  return `$${value.toLocaleString()}`;
};

const formatDate = (value: string, language: string) => {
  return new Intl.DateTimeFormat(getLocale(language), {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(value));
};

const QuotesList = () => {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const quotes = useSelector(selectQuotesList);
  const loading = useSelector(selectQuotesLoading);

  useEffect(() => {
    if (quotes.length === 0) {
      dispatch(quotesActions.getQuotesList());
    }
  }, [dispatch, quotes.length]);

  const handleRowClick = (row: Quote) => {
    navigate(`/quotes/${row.id}`);
  };

  const getStatusClassName = (status: QuoteStatus) => {
    switch (status) {
      case QuoteStatus.DRAFT:
        return `${styles.statusChip} ${styles.draftChip}`;

      case QuoteStatus.SENT:
        return `${styles.statusChip} ${styles.sentChip}`;

      case QuoteStatus.ACCEPTED:
        return `${styles.statusChip} ${styles.acceptedChip}`;

      default:
        return styles.statusChip;
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "validUntil",
        label: t("QUOTES.TABLE.VALID_UNTIL"),
        minWidth: 130,
        align: "right",
        format: (row: Quote) => formatDate(row.validUntil, i18n.language),
      },
      {
        id: "status",
        label: t("QUOTES.TABLE.STATUS"),
        minWidth: 110,
        align: "right",
        format: (row: Quote) => (
          <span className={getStatusClassName(row.status)}>
            {t(selectQuoteStatusLabelKey(row.status))}
          </span>
        ),
      },
      {
        id: "totalValue",
        label: t("QUOTES.TABLE.TOTAL_VALUE"),
        minWidth: 130,
        align: "right",
        format: (row: Quote) => formatCurrency(row.totalValue),
      },
      {
        id: "revisionNumber",
        label: t("QUOTES.TABLE.REVISION_NUMBER"),
        minWidth: 150,
        align: "right",
        format: (row: Quote) => `${row.revisionNumber}.0`,
      },
      {
        id: "projectTypeKey",
        label: t("QUOTES.TABLE.PROJECT_TYPE"),
        minWidth: 220,
        align: "right",
        format: (row: Quote) => t(row.projectTypeKey),
      },
      {
        id: "customerNameKey",
        label: t("QUOTES.TABLE.CUSTOMER_NAME"),
        minWidth: 180,
        align: "right",
        format: (row: Quote) => t(row.customerNameKey),
      },
      {
        id: "createdAt",
        label: t("QUOTES.TABLE.CREATED_AT"),
        minWidth: 140,
        align: "right",
        format: (row: Quote) => formatDate(row.createdAt, i18n.language),
      },
      {
        id: "quoteNumber",
        label: t("QUOTES.TABLE.QUOTE_NUMBER"),
        minWidth: 100,
        align: "right",
      },
    ],
    [i18n.language, styles, t]
  );

  return (
    <Box className={styles.root} dir={i18n.dir()}>
      <ActionBar />

      <Box className={styles.tableWrapper}>
        <DataTable
          rows={quotes}
          columns={columns}
          loading={loading && quotes.length === 0}
          serialEnabled={false}
          onRowClick={handleRowClick}
          isPointerCursor
          noDataLabel={t("QUOTES.EMPTY")}
        />
      </Box>
    </Box>
  );
};

export default QuotesList;